import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import {
  getTagCategories,
  createTagCategory,
  updateTagCategory,
  deleteTagCategory,
} from '@/services/api/tag.service'
import type { TagTargetType } from '@/types/api/tag'

export function useTagCategories(recordGroupId: string | undefined, targetType: TagTargetType) {
  const queryClient = useQueryClient()
  const queryKey = ['tag-categories', recordGroupId, targetType]
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey })
    // Deleting a category uncategorizes its tags.
    queryClient.invalidateQueries({ queryKey: ['tags', recordGroupId, targetType] })
  }

  const query = useQuery({
    queryKey,
    queryFn: () => getTagCategories(recordGroupId!, targetType),
    enabled: !!recordGroupId,
    staleTime: 60 * 1000,
  })

  const createMutation = useMutation({
    mutationFn: (body: { name: string | null; singleSelect: boolean }) =>
      createTagCategory(recordGroupId!, { ...body, targetType }),
    onSuccess: invalidate,
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, name, singleSelect }: { id: string; name: string | null; singleSelect: boolean }) =>
      updateTagCategory(id, { targetType, name, singleSelect }),
    onSuccess: invalidate,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTagCategory(id),
    onSuccess: invalidate,
  })

  return {
    categories: query.data ?? [],
    isLoading: query.isLoading,
    createCategory: createMutation.mutate,
    isCreating: createMutation.isPending,
    updateCategory: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    deleteCategory: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  }
}
